import React from "react";
import {Modal as BootStrapModal} from "react-bootstrap";
import styles from "./PaymentModal.module.css";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBank, faCopy, faExclamationTriangle} from "@fortawesome/free-solid-svg-icons";
import {useTranslation} from "react-i18next";

export default function PaymentModal({show, plan, bankInfo, onClose, onConfirm, requesting, error, billingCycle, pricing}) {
    const {t} = useTranslation();

    if (!plan) return null;

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const isYearly = billingCycle === 'yearly';

    return (
        <BootStrapModal show={show} onHide={onClose} centered size="lg">
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h2 className={styles.title}>{t('payment.completeYourPurchase')}</h2>
                    <button className={styles.closeButton} onClick={onClose}>&times;</button>
                </div>

                <div className={styles.body}>
                    <div className={styles.planSummary}>
                        <div className={styles.planInfo}>
                            <span className={styles.planLabel}>{t('payment.selectedPlan')}</span>
                            <span className={styles.planName}>{plan.name}</span>
                            <span className={styles.billingCycleBadge}>
                                {isYearly ? t('payment.annualPayment') : t('payment.monthlyPayment')}
                            </span>
                        </div>
                        <div className={styles.planPrice}>
                            <span className={styles.amount}>${pricing?.finalPrice?.toFixed(2) || plan.price.toFixed(2)}</span>
                            <span className={styles.period}>USD/{isYearly ? t('payment.year') : t('payment.month')}</span>
                        </div>
                    </div>

                    <div className={styles.priceBreakdown}>
                        {isYearly && pricing?.savings > 0 && (
                            <div className={`${styles.priceRow} ${styles.savingsRow}`}>
                                <span>{t('payment.baseMonthlyPrice')}</span>
                                <span>{`$${pricing.monthlyPrice.toFixed(2)} ${t('payment.months')}`}</span>
                            </div>
                        )}
                        {isYearly && pricing?.discountApplied && (
                            <div className={`${styles.priceRow} ${styles.discountRow}`}>
                                <span>{t('payment.annualDiscount', {discount: pricing.discountApplied})}</span>
                                <span className={styles.discountAmount}>-${pricing.savings.toFixed(2)}</span>
                            </div>
                        )}
                        <div className={styles.priceRow}>
                            <span>{t('payment.subtotal', {period: pricing?.period || '1 month'})}</span>
                            <span>${pricing?.finalPrice?.toFixed(2) || plan.price.toFixed(2)}</span>
                        </div>
                        {pricing?.showIGV && (
                            <div className={styles.priceRow}>
                                <span>{t('payment.igv')}</span>
                                <span>${pricing?.tax?.toFixed(2) || '0.00'}</span>
                            </div>
                        )}
                        <div className={`${styles.priceRow} ${styles.totalRow}`}>
                            <span>{t('payment.totalToPay')}</span>
                            <span className={styles.totalAmount}>${pricing?.finalPriceWithTax?.toFixed(2) || plan.price.toFixed(2)} USD</span>
                        </div>
                    </div>

                    {error && (
                        <div className={styles.error}>
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className={styles.bankSection}>
                        <div className={styles.bankHeader}>
                            <FontAwesomeIcon icon={faBank} className={styles.bankIcon} />
                            <h3>{t('payment.bankTransferDetails')}</h3>
                        </div>

                        {bankInfo ? (
                            <div className={styles.bankDetails}>
                                <div className={styles.bankRow}>
                                    <span className={styles.bankLabel}>{t('payment.company')}</span>
                                    <span className={styles.bankValue}>{bankInfo.company_name}</span>
                                </div>
                                <div className={styles.bankRow}>
                                    <span className={styles.bankLabel}>{t('payment.address')}</span>
                                    <span className={styles.bankValue}>{bankInfo.address}</span>
                                </div>
                                <div className={styles.bankRow}>
                                    <span className={styles.bankLabel}>{t('payment.bank')}</span>
                                    <span className={styles.bankValue}>{bankInfo.bank}</span>
                                </div>
                                <div className={styles.bankRow}>
                                    <span className={styles.bankLabel}>{t('payment.city')}</span>
                                    <span className={styles.bankValue}>{bankInfo.city}, {bankInfo.country}</span>
                                </div>
                                <div className={styles.bankRow}>
                                    <span className={styles.bankLabel}>{t('payment.usdAccount')}</span>
                                    <div className={styles.copyableValue}>
                                        <span className={styles.bankValue}>{bankInfo.account_usd}</span>
                                        <button
                                            className={styles.copyButton}
                                            onClick={() => copyToClipboard(bankInfo.account_usd)}
                                            title="Copy"
                                        >
                                            <FontAwesomeIcon icon={faCopy} />
                                        </button>
                                    </div>
                                </div>
                                <div className={styles.bankRow}>
                                    <span className={styles.bankLabel}>{t('payment.swift')}</span>
                                    <div className={styles.copyableValue}>
                                        <span className={styles.bankValue}>{bankInfo.swift}</span>
                                        <button
                                            className={styles.copyButton}
                                            onClick={() => copyToClipboard(bankInfo.swift)}
                                            title="Copy"
                                        >
                                            <FontAwesomeIcon icon={faCopy} />
                                        </button>
                                    </div>
                                </div>

                                {bankInfo.instructions && (
                                    <div className={styles.instructions}>
                                        <strong>{t('payment.important')}</strong> {bankInfo.instructions}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className={styles.noBankInfo}>
                                {t('payment.bankInfoNotFound')}
                            </div>
                        )}
                    </div>

                    <div className={styles.notice}>
                        <p>{t('payment.notice')}</p>
                    </div>
                </div>

                <div className={styles.footer}>
                    <button className={styles.cancelButton} onClick={onClose} disabled={requesting}>
                        {t('payment.cancel')}
                    </button>
                    <button
                        className={styles.confirmButton}
                        onClick={onConfirm}
                        disabled={requesting || !bankInfo}
                    >
                        {requesting ? t('payment.processing') : t('payment.confirmRequest')}
                    </button>
                </div>
            </div>
        </BootStrapModal>
    );
}
